import { useMemo, useState } from "react"
import { useToast } from "@mallardbay/lib-react-components"

import type { SearchOutfittersForImpersonationQuery } from "~graphql/users.generated"
import { useSearchOutfittersForImpersonationQuery } from "~graphql/users.generated"
import { useDebounce } from "~utils/hooks/use-debounce"

type SearchOutfitterResults =
    SearchOutfittersForImpersonationQuery["searchOutfittersForImpersonation"]["results"]

export type ImpersonationSearchResult = SearchOutfitterResults[number]
export type ImpersonationMember = ImpersonationSearchResult["members"][number]

export default function useSearchOutfittersForImpersonation({
    isEnabled,
    excludedUserId,
}: {
    isEnabled: boolean
    excludedUserId?: string
}) {
    const { showError } = useToast()
    const [query, setQuery] = useState("")
    const debouncedQuery = useDebounce(query, 500).trim()

    const { loading: isLoading, data } =
        useSearchOutfittersForImpersonationQuery({
            variables: { text: debouncedQuery, page: 0, size: 8 },
            skip: !isEnabled,
            onError: showError,
        })

    const results = useMemo(
        () =>
            getFilteredResults({
                results: data?.searchOutfittersForImpersonation.results ?? [],
                excludedUserId,
            }),
        [data?.searchOutfittersForImpersonation.results, excludedUserId]
    )
    const total = data?.searchOutfittersForImpersonation.total ?? 0

    return { results, total, isLoading, query, setQuery }
}

function getFilteredResults({
    results,
    excludedUserId,
}: {
    results: SearchOutfitterResults
    excludedUserId?: string
}): SearchOutfitterResults {
    if (!excludedUserId) return results

    return results.reduce<SearchOutfitterResults>((acc, result) => {
        const filteredMembers = result.members.filter(
            ({ user }) => user.id !== excludedUserId
        )

        if (filteredMembers.length === 0) return acc

        const isAutoSelectedUserExcluded =
            result.auto_select_member?.user.id === excludedUserId
        acc.push({
            ...result,
            members: filteredMembers,
            auto_select_member: isAutoSelectedUserExcluded
                ? null
                : result.auto_select_member,
        })

        return acc
    }, [])
}
